import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import SimpleDrawer from '@/components/drawers/simple-drawer'
import orgamax from 'ampersand-app'
import { debounce } from 'lodash'
import config from '@/config'
import settingsConfig from '@/modules/settings/config'
import { TextInput, HtmlInput, Checkbox } from '@/modules/base/components/inputs'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import IconEmail from '@assets/images/icons/mail.svg'
import RecipientEmailInput from '@/modules/base/components/inputs/recipient-email-input/recipient-email-input'
import { SelectInput } from '@/modules/base/components/inputs/index'

import { toast } from '@/modules/base/components/toast/toastConfig'
import EmailInput from '@/modules/base/components/inputs/email-input/email-input'
import { useSelector } from 'react-redux'
import customDecodeUri from '@/utils/customDecodeUri'
import EmailService from '@/modules/_services/email.services'
import { EMAIL_ACCOUNT_TYPE } from '@/modules/base/util/constants'
import request from '@/modules/base/util/request'
import { useNavigate } from 'react-router'
import { formatSenderLabel } from '@/utils/sender-email'
import EmailNotConfirmedHint from '@/modules/account/components/confirm-email/email-not-confirmed-hint'
import { handleEmailNotConfirmedError } from '@/utils/handleEmailNotConfirmedError'
import { isMeinBuero } from '@/utils/build-env'

const isMB = isMeinBuero()

const SendEmailDrawer = (props) => {
  const navigate = useNavigate()
  const { activeTenant } = useSelector(state => state.user.info)
  const { senderEmail, senderEmailName, tenantName } = activeTenant
  // DO-13065: sending is refused until the sender's address is verified
  const hasConfirmedEmail = useSelector(state => state.subscription.details.confirmation?.hasConfirmedEmail)
  const isSendBlockedByEmailConfirmation = !isMB && hasConfirmedEmail === false

  const classes = useStyles(props)

  const {
    onClose,
    open,
    file: { id, name, customer },
    eSign,
  } = props
  const [formData, setFormData] = useState({ })

  useEffect(() => {
    setFormData({
      subject: lang.documents.email.subject(customDecodeUri(name), eSign),
      text: lang.documents.email.text(senderEmailName ?? tenantName ?? senderEmail, eSign),
      sendCopy: false,
    })
  }, [id])

  const [recipients, setRecipients] = useState(null)
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const [email, setEmail] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [systemEmail, setSystemEmail] = useState(null)
  const isSystemEmailSelected = !!systemEmail?.address && email?.value === systemEmail.address
  const showSenderSettingsHint = isSystemEmailSelected && !senderEmailName?.trim()

  useEffect(() => {
    setIsButtonDisabled(!(formData.subject && recipients && recipients.length > 0) || isLoading)
  }, [formData, recipients, isLoading])

  const onInputChange = (value, name) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }))
  }

  const debouncedInputChange = useCallback(
    debounce(onInputChange, 500), [],
  )

  const onSendSuccess = () => {
    setIsLoading(false)
    onClose()
    toast.showToast({ message: lang.transactionSendEmailSuccessMessage })
  }

  const onSendError = (error) => {
    setIsButtonDisabled(false)
    setIsLoading(false)
    if (!handleEmailNotConfirmedError(error)) {
      toast.showToast({ message: lang.defaultSendEmailErrorMessage, type: 'error' })
    }
    throw error
  }

  const getSystemEmailOption = (address) => ({
    label: formatSenderLabel(address, senderEmailName),
    value: address,
    ...(senderEmailName?.trim() ? {} : { tooltip: lang.transactionSendEmail.missingSenderNameTooltip }),
  })

  const goToSenderSettings = () => {
    navigate('/settings/myaccount#email')
  }

  const onButtonClick = () => {
    setIsLoading(true)
    const endpoint = eSign ? `file/${id}/eSign` : `file/${id}/send`
    const emails = recipients.map((recipient) => {
      return recipient.email
    })

    return request(`${config.resourceHost}${endpoint}`, {
      method: 'POST',
      auth: true,
      data: {
        recipients: emails,
        recipientName: formData.recipientName,
        subject: formData.subject,
        text: formData.text,
        sendCopy: formData.sendCopy,
        emailAccountId: email.id,
      },
    })
      .then(onSendSuccess)
      .catch(onSendError)
  }

  const fetchEmailAccounts = () => {
    return Promise.all([
      request(settingsConfig.getEmailServiceSetup, { auth: true, isConcurrent: true }),
      request(settingsConfig.getSystemEmail, { auth: true, isConcurrent: true }),
    ])
      .then(([emailServiceResponse, systemEmailResponse]) => {
        const { body: { data: { useOwnEmailServer } } } = emailServiceResponse
        const { body: { data: systemEmailData } } = systemEmailResponse

        setSystemEmail(systemEmailData)

        const systemEmailOption = systemEmailData?.address ? getSystemEmailOption(systemEmailData.address) : null

        if (useOwnEmailServer === false) {
          if (!systemEmailOption) {
            return []
          }

          setEmail(systemEmailOption)
          return [systemEmailOption]
        } else if (useOwnEmailServer === true) {
          return EmailService.fetchEmailAccounts(EMAIL_ACCOUNT_TYPE.OUTGOING).then((emails = []) => {
            const toAccountOption = (account) => {
              const address = account.email ?? account?.settings?.email

              return {
                label: formatSenderLabel(address, account?.settings?.senderName),
                value: address,
                id: account.id,
              }
            }
            const defaultEmail = emails.find(email => email.isDefault)

            if (defaultEmail) {
              setEmail(toAccountOption(defaultEmail))
            }

            return emails.map(toAccountOption)
          })
        }

        return []
      })
      .catch(() => orgamax.toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }

  const handleEmailChange = (option) => {
    setEmail(option)
  }

  const parseEmailData = (emailAddress) => {
    if (config.emailCheck.test(emailAddress)) {
      setRecipients(() => [{ email: emailAddress }])
    }
  }

  const customHandleBlur = (value, name, event, handleBlur) => {
    if (event && value) {
      parseEmailData(value)
    }
  }

  return (
    <SimpleDrawer className={classes.drawer}
      open={open}
      headline={lang.documents.email.header}
      width='486px'
      onClose={onClose}
    >
      <div className={classes.form}>
        {isSendBlockedByEmailConfirmation && (
          <EmailNotConfirmedHint className={classes.inputWrapper} dataQsId='document-send-email-not-confirmed-hint' />
        )}
        <SelectInput
          label={lang.transactionSendEmail.emailSelectInput}
          className={classes.inputWrapper}
          notAsync={false}
          customSelectOptions={{
            loadOptions: fetchEmailAccounts,
            defaultOptions: true,
            valueKey: 'value',
            labelKey: 'label',
          }}
          handleChange={handleEmailChange}
          value={email}
          dataQsId='document-send-email-sender-select'
        />
        {showSenderSettingsHint && (
          <div className={classes.senderSettingsHint} onClick={goToSenderSettings}>
            {lang.transactionSendEmail.adjustDisplayedSender}
          </div>
        )}
        <TextInput
          name='subject'
          label={lang.transactionSendEmail.subject}
          required={true}
          value={formData.subject}
          onChange={debouncedInputChange}
          customClass={classes.inputWrapper}
          useFormik={false}
        />
        {eSign
          ? <TextInput
            name='recipientName'
            label={lang.transactionSendEmail.recipientName}
            required={true}
            value={formData.recipientName}
            onChange={debouncedInputChange}
            customClass={classes.inputWrapper}
            useFormik={false}
          />
          : null}
        {customer
          ? <RecipientEmailInput
            customerId={customer.id}
            customClass={classes.inputWrapper}
            onChange={setRecipients}
          />
          : <EmailInput className={classes.emailInput} required
            label={lang.emailLabel}
            onBlur={(value, name, event) => customHandleBlur(value, name, event)} />}
        <HtmlInput
          name='text'
          label={lang.transactionSendEmail.messageCustomer}
          value={formData.text}
          onBlur={debouncedInputChange}
          wrapperClass={classes.inputWrapper}
          useFormik={false}
          excludedFormats={['Link', 'placeholder', 'mjmlButton']}
        />
      </div>
      <div className={classes.formFooter}>
        <Checkbox
          name='sendCopy'
          checked={formData.sendCopy}
          onChange={onInputChange}>
          <div className={classes.checkboxLabel}>
            {lang.transactionSendEmail.sendCopy}
          </div>
        </Checkbox>
        <ButtonIconSquare
          label={lang.transactionSendEmail.sendEmail}
          icon={IconEmail}
          onClick={onButtonClick}
          disabled={isButtonDisabled || isSendBlockedByEmailConfirmation}
          dataQsId='document-send-email-send-button'
        />
      </div>
    </SimpleDrawer>
  )
}

SendEmailDrawer.propTypes = {
  file: PropTypes.object,
  open: PropTypes.bool,
  eSign: PropTypes.bool,
  onClose: PropTypes.func,
}

const useStyles = makeStyles((theme) => ({
  drawer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
  },
  transactionSendEmailWrapper: {
    margin: '30px auto 0',
    width: '840px',
  },
  form: {
    padding: 30,
  },
  emailInput: {
    marginBottom: 20,
  },
  inputWrapper: {
    marginBottom: '20px',
  },
  senderSettingsHint: {
    color: theme.primary,
    cursor: 'pointer',
    fontSize: '12px',
    marginTop: '-12px',
    marginBottom: '20px',
    textDecoration: 'underline',
  },
  toggleWrapper: {
    display: 'flex',
    marginBottom: '30px',
  },
  checkboxLabel: {
    marginLeft: '15px',
  },
  formFooter: {
    padding: 30,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  loaderWrapper: {
    background: 'transparent',
    position: 'relative',
  },
}))

export default SendEmailDrawer
